import React, { useEffect, useMemo, useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";
import { brl, todayISO } from "@/lib/format";
import { FormModal, TextField } from "@/components/FormKit";

const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
export const monthLabel = (ym) => {
  const [y, m] = (ym || "").split("-").map(Number);
  return m >= 1 && m <= 12 ? `${MONTHS[m - 1]}/${y}` : ym;
};
export const sumAmount = (rows) => rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

export function BulkDeleteBar({ rows, settledStatus, settledLabel, onDelete, onClear, prefix }) {
  if (!rows.length) return null;
  const settled = rows.filter((r) => r.status === settledStatus).length;
  return (
    <div data-testid={`${prefix}-bulk-bar`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 animate-in fade-in slide-in-from-top-1">
      <div className="text-sm text-rose-900">
        <strong data-testid={`${prefix}-bulk-count`}>{rows.length}</strong> lançamento(s) selecionado(s) · Total <strong className="font-mono">{brl(sumAmount(rows))}</strong>
        {settled > 0 && <span className="text-rose-700"> · {settled} já {settledLabel} (saldo será estornado)</span>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={onClear} data-testid={`${prefix}-bulk-clear`} className="px-3 py-2 rounded-lg border border-rose-200 text-rose-700 text-sm font-medium hover:bg-rose-100">Limpar seleção</button>
        <button onClick={onDelete} data-testid={`${prefix}-bulk-delete`} className="bg-rose-600 hover:bg-rose-700 text-white font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2 shadow-sm"><Trash2 className="h-4 w-4" /> Excluir selecionados</button>
      </div>
    </div>
  );
}

export function MonthDeleteModal({ open, onOpenChange, rows, settledStatus, settledLabel, onConfirm, submitting, prefix }) {
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  useEffect(() => { if (open) setMonth(todayISO().slice(0, 7)); }, [open]);
  const inMonth = useMemo(() => rows.filter((r) => (r.due_date || "").slice(0, 7) === month), [rows, month]);
  const settled = inMonth.filter((r) => r.status === settledStatus).length;
  return (
    <FormModal open={open} onOpenChange={onOpenChange} title="Excluir lançamentos do mês (Administrador)" onSubmit={(e) => { e.preventDefault(); onConfirm(month, inMonth.length); }}
      submitting={submitting} testId={`${prefix}-month-delete-form`} width="max-w-md" submitLabel={`Excluir ${inMonth.length} lançamento(s)`}>
      <TextField label="Mês de vencimento" type="month" value={month} onChange={setMonth} testId={`${prefix}-month-input`} required />
      <div className="bg-rose-50 border border-rose-200 rounded-lg px-4 py-3 text-sm text-rose-900 space-y-1" data-testid={`${prefix}-month-summary`}>
        <div>Lançamentos com vencimento em <strong>{monthLabel(month)}</strong>: <strong data-testid={`${prefix}-month-count`}>{inMonth.length}</strong></div>
        <div>Total: <strong className="font-mono">{brl(sumAmount(inMonth))}</strong></div>
        {settled > 0 && <div>{settled} já {settledLabel} — o saldo bancário será estornado automaticamente.</div>}
        <div className="flex items-start gap-2 text-xs text-rose-700 pt-1"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Esta ação não pode ser desfeita. Os registros saem das listagens e relatórios; o histórico permanece na auditoria.</div>
      </div>
    </FormModal>
  );
}
